/**
 * Estado do aplicativo: servidores, canais, membros, mensagens e presença.
 *
 * Tudo indexado por id em objetos planos. Guardar listas aninhadas obrigaria a
 * varrer e reconstruir árvores a cada evento do gateway, o que numa comunidade
 * ativa significa recalcular a interface inteira várias vezes por segundo.
 */

import { create } from 'zustand'
import type {
  Category,
  Channel,
  Guild,
  GuildMember,
  Message,
  Presence,
  ReadState,
  Role,
  ServerEvent,
  VoiceState,
} from '@nexus/shared'
import { api } from '@/lib/api'
import { gateway } from '@/lib/gateway'
import { tocarSom } from '@/lib/bandeja-de-sons'
import { usePreferencesStore } from '@/store/preferences'
import { useVoiceStore } from '@/store/voice'

type Dict<T> = Record<string, T>

interface TypingEntry {
  userId: string
  expiresAt: number
}

interface AppState {
  ready: boolean

  guilds: Dict<Guild>
  channels: Dict<Channel>
  categories: Dict<Category>
  roles: Dict<Role>
  /** Chave composta `guildId:userId`. */
  members: Dict<GuildMember>
  presences: Dict<Presence>
  voiceStates: Dict<VoiceState>
  readStates: Dict<ReadState>

  /** Mensagens por canal, sempre em ordem cronológica. */
  messages: Dict<Message[]>
  /** Canais cujo histórico já foi carregado ao menos uma vez. */
  loadedChannels: Set<string>
  loadingChannels: Set<string>
  /** Canais que ainda têm mensagens mais antigas no servidor. */
  hasMoreBefore: Dict<boolean>
  /**
   * Canais mostrando um trecho do passado em vez do fim da conversa.
   *
   * Acontece ao pular para uma mensagem fixada antiga: a lista passa a ser a
   * vizinhança dela, e o presente fica fora do que está carregado.
   */
  emTrecho: Dict<boolean>

  typing: Dict<TypingEntry[]>

  currentGuildId: string | null
  currentChannelId: string | null

  applyEvent: (event: ServerEvent) => void
  selectGuild: (guildId: string) => void
  selectChannel: (channelId: string) => void
  loadMessages: (channelId: string) => Promise<void>
  loadOlderMessages: (channelId: string) => Promise<void>
  /** Carrega a vizinhança de uma mensagem, para poder mostrá-la na tela. */
  carregarAoRedor: (channelId: string, messageId: string) => Promise<boolean>
  /** Volta do trecho antigo para o fim da conversa. */
  voltarAoPresente: (channelId: string) => Promise<void>
  sendMessage: (channelId: string, content: string, replyToId?: string | null, attachmentIds?: string[]) => Promise<void>
  markRead: (channelId: string) => void
  reset: () => void

  // Seletores derivados
  guildChannels: (guildId: string) => { categories: Category[]; byCategory: Dict<Channel[]>; uncategorized: Channel[] }
  guildMembers: (guildId: string) => GuildMember[]
  channelVoiceStates: (channelId: string) => VoiceState[]
  memberOf: (guildId: string, userId: string) => GuildMember | undefined
  typingIn: (channelId: string) => string[]
  unreadCount: (channelId: string) => number
}

const memberKey = (guildId: string, userId: string) => `${guildId}:${userId}`

function indexBy<T>(items: T[], key: (item: T) => string): Dict<T> {
  const result: Dict<T> = {}
  for (const item of items) result[key(item)] = item
  return result
}

/** Insere mantendo a ordem cronológica e sem duplicar por reenvio de evento. */
function insertMessage(list: Message[], message: Message): Message[] {
  const existingIndex = list.findIndex((m) => m.id === message.id)
  if (existingIndex >= 0) {
    const next = [...list]
    next[existingIndex] = message
    return next
  }

  // O caso comum é a mensagem ser a mais nova, então testamos isso primeiro.
  if (list.length === 0 || BigInt(message.id) > BigInt(list[list.length - 1]!.id)) {
    return [...list, message]
  }

  const index = list.findIndex((m) => BigInt(m.id) > BigInt(message.id))
  if (index < 0) return [...list, message]
  return [...list.slice(0, index), message, ...list.slice(index)]
}

export const useAppStore = create<AppState>((set, get) => ({
  ready: false,

  guilds: {},
  channels: {},
  categories: {},
  roles: {},
  members: {},
  presences: {},
  voiceStates: {},
  readStates: {},
  messages: {},
  loadedChannels: new Set(),
  loadingChannels: new Set(),
  hasMoreBefore: {},
  emTrecho: {},
  typing: {},

  currentGuildId: null,
  currentChannelId: null,

  applyEvent: (event) => {
    switch (event.op) {
      case 'ready': {
        const d = event.d
        set({
          ready: true,
          guilds: indexBy(d.guilds, (g) => g.id),
          channels: indexBy(d.channels, (c) => c.id),
          categories: indexBy(d.categories, (c) => c.id),
          roles: indexBy(d.roles, (r) => r.id),
          members: indexBy(d.members, (m) => memberKey(m.guildId, m.userId)),
          presences: indexBy(d.presences, (p) => p.userId),
          voiceStates: indexBy(
            d.voiceStates.filter((v) => v.channelId),
            (v) => v.userId,
          ),
          readStates: indexBy(d.readStates, (r) => r.channelId),
        })

        // Seleciona o primeiro servidor automaticamente. Abrir num estado vazio
        // faria parecer que nada carregou.
        const state = get()
        if (!state.currentGuildId && d.guilds.length > 0) {
          get().selectGuild(d.guilds[0]!.id)
        }
        return
      }

      case 'message_create':
      case 'message_update': {
        const message = event.d
        set((state) => {
          const list = state.messages[message.channelId] ?? []
          const isNew = event.op === 'message_create'
          const isCurrent = state.currentChannelId === message.channelId
          /*
            Com a lista mostrando um trecho do passado, a mensagem nova não tem
            onde encaixar: colada ali embaixo, ela apareceria logo depois de uma
            conversa de outro dia, com todo o meio faltando. Ela chega quando a
            pessoa voltar para o fim, que é o momento em que a lista volta a ser
            contígua.
          */
          const congelada = state.emTrecho[message.channelId] === true

          const readState = state.readStates[message.channelId]
          const nextRead =
            isNew && isCurrent
              ? { ...(readState ?? { channelId: message.channelId, mentionCount: 0 }), lastReadMessageId: message.id }
              : readState

          return {
            messages: congelada
              ? state.messages
              : { ...state.messages, [message.channelId]: insertMessage(list, message) },
            channels: state.channels[message.channelId]
              ? state.channels
              : state.channels,
            readStates: nextRead
              ? { ...state.readStates, [message.channelId]: nextRead as ReadState }
              : state.readStates,
            // Quem acabou de mandar mensagem não está mais digitando.
            typing: {
              ...state.typing,
              [message.channelId]: (state.typing[message.channelId] ?? []).filter(
                (t) => t.userId !== message.authorId,
              ),
            },
          }
        })
        return
      }

      case 'message_delete': {
        set((state) => ({
          messages: {
            ...state.messages,
            [event.d.channelId]: (state.messages[event.d.channelId] ?? []).filter(
              (m) => m.id !== event.d.id,
            ),
          },
        }))
        return
      }

      case 'reaction_update': {
        set((state) => ({
          messages: {
            ...state.messages,
            [event.d.channelId]: (state.messages[event.d.channelId] ?? []).map((m) =>
              m.id === event.d.messageId ? { ...m, reactions: event.d.reactions } : m,
            ),
          },
        }))
        return
      }

      case 'typing_start': {
        const expiresAt = new Date(event.d.expiresAt).getTime()
        set((state) => {
          const current = (state.typing[event.d.channelId] ?? []).filter(
            (t) => t.userId !== event.d.userId && t.expiresAt > Date.now(),
          )
          return {
            typing: {
              ...state.typing,
              [event.d.channelId]: [...current, { userId: event.d.userId, expiresAt }],
            },
          }
        })
        return
      }

      case 'presence_update': {
        set((state) => ({ presences: { ...state.presences, [event.d.userId]: event.d } }))
        return
      }

      case 'sound_play': {
        const voz = useVoiceStore.getState()
        // Só toca para quem está na mesma chamada. O evento vai para o servidor
        // inteiro porque é assim que o barramento entrega, e filtrar aqui é mais
        // barato do que manter uma lista de destinatários por sala.
        if (voz.channelId !== event.d.channelId) return
        // Quem parou de ouvir a sala parou de ouvir tudo, inclusive isto.
        if (voz.deafened) return

        const { audio } = usePreferencesStore.getState()
        void tocarSom(event.d.url, {
          volume: audio.volumeDaBandeja,
          outputDeviceId: audio.outputDeviceId,
        })
        return
      }

      case 'voice_state_update': {
        set((state) => {
          const next = { ...state.voiceStates }
          if (event.d.channelId) next[event.d.userId] = event.d
          else delete next[event.d.userId]
          return { voiceStates: next }
        })
        return
      }

      case 'channel_create':
      case 'channel_update': {
        set((state) => ({ channels: { ...state.channels, [event.d.id]: event.d } }))
        return
      }

      case 'channel_delete': {
        set((state) => {
          const channels = { ...state.channels }
          delete channels[event.d.id]
          const messages = { ...state.messages }
          delete messages[event.d.id]
          return {
            channels,
            messages,
            currentChannelId: state.currentChannelId === event.d.id ? null : state.currentChannelId,
          }
        })
        return
      }

      case 'category_create':
      case 'category_update': {
        set((state) => ({ categories: { ...state.categories, [event.d.id]: event.d } }))
        return
      }

      case 'category_delete': {
        set((state) => {
          const categories = { ...state.categories }
          delete categories[event.d.id]
          return { categories }
        })
        return
      }

      case 'guild_create': {
        const d = event.d
        set((state) => ({
          guilds: { ...state.guilds, [d.guild.id]: d.guild },
          channels: { ...state.channels, ...indexBy(d.channels, (c) => c.id) },
          categories: { ...state.categories, ...indexBy(d.categories, (c) => c.id) },
          roles: { ...state.roles, ...indexBy(d.roles, (r) => r.id) },
          members: { ...state.members, ...indexBy(d.members, (m) => memberKey(m.guildId, m.userId)) },
        }))
        return
      }

      case 'guild_update': {
        set((state) => ({ guilds: { ...state.guilds, [event.d.id]: event.d } }))
        return
      }

      case 'guild_delete': {
        set((state) => {
          const guilds = { ...state.guilds }
          delete guilds[event.d.id]

          const channels = { ...state.channels }
          for (const [id, channel] of Object.entries(channels)) {
            if (channel.guildId === event.d.id) delete channels[id]
          }

          const remaining = Object.keys(guilds)
          const wasCurrent = state.currentGuildId === event.d.id

          return {
            guilds,
            channels,
            currentGuildId: wasCurrent ? (remaining[0] ?? null) : state.currentGuildId,
            currentChannelId: wasCurrent ? null : state.currentChannelId,
          }
        })
        return
      }

      case 'member_add':
      case 'member_update': {
        set((state) => ({
          members: {
            ...state.members,
            [memberKey(event.d.guildId, event.d.userId)]: event.d,
          },
        }))
        return
      }

      case 'member_remove': {
        set((state) => {
          const members = { ...state.members }
          delete members[memberKey(event.d.guildId, event.d.userId)]
          return { members }
        })
        return
      }

      case 'role_create':
      case 'role_update': {
        set((state) => ({ roles: { ...state.roles, [event.d.id]: event.d } }))
        return
      }

      case 'role_delete': {
        set((state) => {
          const roles = { ...state.roles }
          delete roles[event.d.id]
          return { roles }
        })
        return
      }

      default:
        return
    }
  },

  selectGuild: (guildId) => {
    const state = get()
    const channels = Object.values(state.channels)
      .filter((c) => c.guildId === guildId && c.type === 'text')
      .sort((a, b) => a.position - b.position)

    // Ao trocar de servidor, cai no primeiro canal de texto visível.
    const first = channels[0]
    set({ currentGuildId: guildId, currentChannelId: first?.id ?? null })

    if (first) void get().loadMessages(first.id)
    gateway.send({ op: 'subscribe_guild', d: { guildId } })
  },

  selectChannel: (channelId) => {
    const channel = get().channels[channelId]
    if (!channel) return

    set({ currentChannelId: channelId, currentGuildId: channel.guildId })

    if (channel.type === 'text') {
      // Quem sai de um canal e volta espera cair no fim da conversa, e não no
      // trecho antigo onde estava quando foi ver uma mensagem fixada.
      if (get().emTrecho[channelId]) void get().voltarAoPresente(channelId)
      else void get().loadMessages(channelId)
      get().markRead(channelId)
    }
  },

  loadMessages: async (channelId) => {
    const state = get()
    if (state.loadedChannels.has(channelId) || state.loadingChannels.has(channelId)) return

    set((s) => ({ loadingChannels: new Set(s.loadingChannels).add(channelId) }))

    try {
      const { messages } = await api.messages.list(channelId, { limit: 50 })
      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        return {
          messages: { ...s.messages, [channelId]: messages },
          loadedChannels: new Set(s.loadedChannels).add(channelId),
          loadingChannels: loading,
          hasMoreBefore: { ...s.hasMoreBefore, [channelId]: messages.length >= 50 },
        }
      })
    } catch {
      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        return { loadingChannels: loading }
      })
    }
  },

  loadOlderMessages: async (channelId) => {
    const state = get()
    const current = state.messages[channelId] ?? []
    if (current.length === 0 || state.loadingChannels.has(channelId)) return
    if (state.hasMoreBefore[channelId] === false) return

    set((s) => ({ loadingChannels: new Set(s.loadingChannels).add(channelId) }))

    try {
      const { messages } = await api.messages.list(channelId, { limit: 50, before: current[0]!.id })
      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        return {
          messages: { ...s.messages, [channelId]: [...messages, ...(s.messages[channelId] ?? [])] },
          loadingChannels: loading,
          hasMoreBefore: { ...s.hasMoreBefore, [channelId]: messages.length >= 50 },
        }
      })
    } catch {
      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        return { loadingChannels: loading }
      })
    }
  },

  /**
   * Carrega a vizinhança de uma mensagem.
   *
   * Serve para chegar numa mensagem fixada que já saiu do trecho carregado. O
   * histórico visível passa a ser aquele pedaço do passado, e não mais o fim da
   * conversa: por isso o canal fica marcado como "em trecho", que é o que faz
   * aparecer o caminho de volta para o presente.
   */
  carregarAoRedor: async (channelId, messageId) => {
    const jaEsta = (get().messages[channelId] ?? []).some((m) => m.id === messageId)
    if (jaEsta) return true

    set((s) => ({ loadingChannels: new Set(s.loadingChannels).add(channelId) }))

    try {
      const { messages } = await api.messages.list(channelId, { around: messageId, limit: 50 })
      const achou = messages.some((m) => m.id === messageId)

      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        if (!achou) return { loadingChannels: loading }

        return {
          messages: { ...s.messages, [channelId]: messages },
          loadingChannels: loading,
          hasMoreBefore: { ...s.hasMoreBefore, [channelId]: true },
          emTrecho: { ...s.emTrecho, [channelId]: true },
        }
      })

      return achou
    } catch {
      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        return { loadingChannels: loading }
      })
      return false
    }
  },

  voltarAoPresente: async (channelId) => {
    if (!get().emTrecho[channelId]) return

    set((s) => ({ loadingChannels: new Set(s.loadingChannels).add(channelId) }))

    try {
      const { messages } = await api.messages.list(channelId, { limit: 50 })
      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        return {
          messages: { ...s.messages, [channelId]: messages },
          loadingChannels: loading,
          hasMoreBefore: { ...s.hasMoreBefore, [channelId]: messages.length >= 50 },
          emTrecho: { ...s.emTrecho, [channelId]: false },
        }
      })
    } catch {
      set((s) => {
        const loading = new Set(s.loadingChannels)
        loading.delete(channelId)
        return { loadingChannels: loading }
      })
    }
  },

  /**
   * Envio otimista: a mensagem aparece na hora com estado pendente e é
   * substituída pela versão do servidor quando ela chega pelo gateway. Numa
   * conexão ruim isso é a diferença entre a interface parecer travada e
   * parecer instantânea.
   */
  sendMessage: async (channelId, content, replyToId, attachmentIds) => {
    // Escrever é o gesto de quem quer conversar agora, não de quem está lendo o
    // passado: a lista volta para o fim antes de a mensagem sair.
    if (get().emTrecho[channelId]) await get().voltarAoPresente(channelId)

    const nonce = `n${Date.now()}${Math.random().toString(36).slice(2, 8)}`

    const message = await api.messages.send(channelId, {
      content,
      replyToId: replyToId ?? null,
      attachmentIds,
      nonce,
    })

    // O mesmo evento chega pelo gateway; `insertMessage` reconcilia por id, de
    // modo que quem chegar primeiro define e o segundo apenas atualiza.
    set((state) => ({
      messages: {
        ...state.messages,
        [channelId]: insertMessage(state.messages[channelId] ?? [], message),
      },
    }))
  },

  markRead: (channelId) => {
    const list = get().messages[channelId] ?? []
    const last = list[list.length - 1]
    if (!last) return

    gateway.send({ op: 'ack_read', d: { channelId, messageId: last.id } })
    set((state) => ({
      readStates: {
        ...state.readStates,
        [channelId]: { channelId, lastReadMessageId: last.id, mentionCount: 0 },
      },
    }))
  },

  reset: () =>
    set({
      ready: false,
      guilds: {},
      channels: {},
      categories: {},
      roles: {},
      members: {},
      presences: {},
      voiceStates: {},
      readStates: {},
      messages: {},
      loadedChannels: new Set(),
      loadingChannels: new Set(),
      hasMoreBefore: {},
      emTrecho: {},
      typing: {},
      currentGuildId: null,
      currentChannelId: null,
    }),

  guildChannels: (guildId) => {
    const state = get()
    const categories = Object.values(state.categories)
      .filter((c) => c.guildId === guildId)
      .sort((a, b) => a.position - b.position)

    const channels = Object.values(state.channels)
      .filter((c) => c.guildId === guildId)
      .sort((a, b) => a.position - b.position)

    const byCategory: Dict<Channel[]> = {}
    const uncategorized: Channel[] = []

    for (const channel of channels) {
      if (channel.categoryId) {
        const list = byCategory[channel.categoryId]
        if (list) list.push(channel)
        else byCategory[channel.categoryId] = [channel]
      } else {
        uncategorized.push(channel)
      }
    }

    return { categories, byCategory, uncategorized }
  },

  guildMembers: (guildId) =>
    Object.values(get().members)
      .filter((m) => m.guildId === guildId)
      .sort((a, b) => {
        const nameA = (a.nickname ?? a.user.displayName).toLowerCase()
        const nameB = (b.nickname ?? b.user.displayName).toLowerCase()
        return nameA.localeCompare(nameB, 'pt-BR')
      }),

  channelVoiceStates: (channelId) =>
    Object.values(get().voiceStates).filter((v) => v.channelId === channelId),

  memberOf: (guildId, userId) => get().members[memberKey(guildId, userId)],

  typingIn: (channelId) => {
    const now = Date.now()
    return (get().typing[channelId] ?? []).filter((t) => t.expiresAt > now).map((t) => t.userId)
  },

  unreadCount: (channelId) => {
    const state = get()
    const read = state.readStates[channelId]
    const list = state.messages[channelId] ?? []
    if (list.length === 0) return 0
    if (!read?.lastReadMessageId) return list.length

    const lastRead = BigInt(read.lastReadMessageId)
    return list.filter((m) => BigInt(m.id) > lastRead).length
  },
}))

/** Liga o gateway ao store. Chamado uma vez na inicialização do aplicativo. */
export function connectStoreToGateway(): () => void {
  return gateway.onEvent((event) => {
    useAppStore.getState().applyEvent(event)
  })
}
